import React from 'react'
import { Route, Routes, Navigate, useLocation } from 'react-router'
import { useSelector } from 'react-redux'

import AppLoader from './components/Loaders/AppLoader'
import AuthorizedRoute from './components/hoc/AuthorizedRoute'

import ThemeSelector from './pages/ThemeSelector'
import Login from './pages/Unauthorized/Login'
import CreatePassword from './pages/Unauthorized/CreatePassword'
import SetForgotPassword from './pages/Unauthorized/SetForgotPassword'
import ResetPassword from './pages/Unauthorized/ResetPassword'
import ResendRegister from './pages/Unauthorized/ResendRegister'
import AcceptInvite from './pages/Unauthorized/AcceptInvite'
import SocialReturnOnInvestmentWrapper from './pages/SocialReturnOnInvestmentWrapper'
import CampaignsPage from './pages/Campaigns'
import PartnershipsPage from './pages/Partnerships'
import RightsSummary from './pages/RightsSummary'
import CampaignsSummaryPage from './pages/CampaignsSummary'
import LineItemsSummaryPage from './pages/LineItemsSummary'
import AdApprovalsPage from './pages/AdApprovals'
import SocialAuthorizeCallback from './pages/SocialAuthorize/SocialAuthorizeCallback'
import AdAccountAccessMissing from './pages/SocialAuthorize/AdAccountAccessMissing'
import Settings from './pages/Settings'
import Invites from './pages/Invites'
// import ChatPage from './pages/ChatPage'

import { selectedPartnershipSelector } from './modules/selectors/partnerships'

import { ROUTES as ROUTES_CONST } from './constants/routes'
import { getDefaultPageUrl } from './helpers/url'

const RemoveTrailingSlash = ({ ...rest }) => {
  const location = useLocation()

  // If the last character of the url is '/'
  if (location.pathname.match('/.*/$')) {
    return (
      <Navigate
        replace
        {...rest}
        to={{
          pathname: location.pathname.replace(/\/+$/, ''),
          search: location.search
        }}
      />
    )
  } else return null
}
export const AuthorizedAccountLessRoutes = () => (
  <React.Fragment>
    {/* the row below is for cutting off trailing slash */}
    <RemoveTrailingSlash />

    <Routes>
      <Route
        path={ROUTES_CONST.invites}
        element={
          <AuthorizedRoute path={ROUTES_CONST.invites}>
            <Invites />
          </AuthorizedRoute>
        }
      />
      <Route
        path={ROUTES_CONST.authorisePlatformCallback}
        element={
          <AuthorizedRoute path={ROUTES_CONST.authorisePlatformCallback}>
            <SocialAuthorizeCallback />
          </AuthorizedRoute>
        }
      />
      {/* Show Loader when redirected to some not existed page default page
      (currently it handles the loading state between authorisePlatformCallback and redirecting to MainAuthorizedApp */}
      <Route path="*" element={<AppLoader showTopMenu />} />
    </Routes>
  </React.Fragment>
)

export const AuthorizedRoutes = () => {
  const selectedPartnership = useSelector(selectedPartnershipSelector)
  const { display_rights: displayRights, display_sroi: displaySroi } = selectedPartnership

  return (
    <React.Fragment>
      {/* the row below is for cutting off trailing slash */}
      <RemoveTrailingSlash />

      <Routes>
        <Route
          path={ROUTES_CONST.themeSelector}
          element={
            <AuthorizedRoute path={ROUTES_CONST.themeSelector}>
              <ThemeSelector />
            </AuthorizedRoute>
          }
        />
        {displayRights && (
          <>
            <Route
              path={ROUTES_CONST.partnerships}
              element={
                <AuthorizedRoute path={ROUTES_CONST.partnerships}>
                  <PartnershipsPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path={ROUTES_CONST.rightsSummary}
              element={
                <AuthorizedRoute path={ROUTES_CONST.rightsSummary}>
                  <RightsSummary />
                </AuthorizedRoute>
              }
            />
            <Route
              path={ROUTES_CONST.campaigns}
              element={
                <AuthorizedRoute path={ROUTES_CONST.campaigns}>
                  <CampaignsPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path={ROUTES_CONST.campaignsSummary}
              element={
                <AuthorizedRoute path={ROUTES_CONST.campaignsSummary}>
                  <CampaignsSummaryPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path={ROUTES_CONST.lineItemsSummary}
              element={
                <AuthorizedRoute path={ROUTES_CONST.lineItemsSummary}>
                  <LineItemsSummaryPage />
                </AuthorizedRoute>
              }
            />
            <Route
              path={ROUTES_CONST.adApprovals}
              element={
                <AuthorizedRoute path={ROUTES_CONST.adApprovals}>
                  <AdApprovalsPage />
                </AuthorizedRoute>
              }
            />
            {/* Chat is temporary hidden, todo uncomment when needed */}
            {/*<Route*/}
            {/*  path={ROUTES_CONST.chat}*/}
            {/*  element={*/}
            {/*    <AuthorizedRoute path={ROUTES_CONST.chat}>*/}
            {/*      <ChatPage />*/}
            {/*    </AuthorizedRoute>*/}
            {/*  }*/}
            {/*/>*/}
          </>
        )}

        {displaySroi && (
          <Route
            path={ROUTES_CONST.socialReturnOnInvestment}
            element={
              <AuthorizedRoute path={ROUTES_CONST.socialReturnOnInvestment}>
                <SocialReturnOnInvestmentWrapper />
              </AuthorizedRoute>
            }
          />
        )}

        <Route
          path={ROUTES_CONST.adAccountAccessMissing}
          element={
            <AuthorizedRoute path={ROUTES_CONST.adAccountAccessMissing}>
              <AdAccountAccessMissing />
            </AuthorizedRoute>
          }
        />

        {/*Settings routes*/}
        <Route
          path={ROUTES_CONST.settings}
          element={
            <AuthorizedRoute path={ROUTES_CONST.settings}>
              <Settings />
            </AuthorizedRoute>
          }
        />
        <Route
          path={`${ROUTES_CONST.settings}/:settingSection`}
          element={
            <AuthorizedRoute path={`${ROUTES_CONST.settings}/:settingSection`}>
              <Settings />
            </AuthorizedRoute>
          }
        />

        {/*Redirect to default page if path doesn't match any of the routes above*/}
        <Route path="*" element={<Navigate to={getDefaultPageUrl(selectedPartnership)} replace />} />
      </Routes>
    </React.Fragment>
  )
}

export const UnauthorizedRoutes = () => (
  <Routes>
    <Route path={ROUTES_CONST.login} element={<Login />} />
    <Route path={ROUTES_CONST.acceptInvite} element={<AcceptInvite />} />
    <Route path={ROUTES_CONST.createPassword} element={<CreatePassword />} />
    <Route path={ROUTES_CONST.resetPassword} element={<ResetPassword />} />
    <Route path={ROUTES_CONST.forgotPassword} element={<SetForgotPassword />} />
    <Route path={ROUTES_CONST.resendRegister} element={<ResendRegister />} />
    <Route path="*" element={<Navigate to={ROUTES_CONST.login} />} />
  </Routes>
)
